import * as React from 'react';
import { cn } from '@/lib/utils/cn';

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'dark' | 'gradient' | 'outline';
  size?: 'sm' | 'md' | 'lg' | 'icon';
  isLoading?: boolean;
}

/**
 * App variant → BlazeUI `bzc-btn` modifier. Note the vocabularies don't line up
 * by name: BlazeUI's `--secondary` is the *dark* button and `--outline` is the
 * light bordered one, so the app's light `secondary` maps to `--outline` and the
 * app's `dark` maps to `--secondary`.
 */
const VARIANT_CLASS: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary: 'bzc-btn--solid',
  secondary: 'bzc-btn--outline',
  ghost: 'bzc-btn--ghost',
  dark: 'bzc-btn--secondary',
  gradient: 'bzc-btn--gradient',
  outline: 'bzc-btn--outline',
};

const SIZE_CLASS: Record<NonNullable<ButtonProps['size']>, string> = {
  sm: 'bzc-btn--sm',
  md: 'bzc-btn--md',
  lg: 'bzc-btn--lg',
  icon: 'bzc-btn--icon-only',
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', isLoading, children, ...props }, ref) => {
    return (
      <button
        ref={ref}
        // `--pill` keeps the app's established fully-rounded shape; call sites can
        // still override the radius (e.g. `rounded-md` icon buttons) because the
        // bzc layer sits below Tailwind utilities.
        className={cn('bzc-btn', VARIANT_CLASS[variant], SIZE_CLASS[size], 'bzc-btn--pill', className)}
        disabled={isLoading || props.disabled}
        {...props}
      >
        {isLoading ? <span className="spin" aria-hidden /> : null}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';

export { Button };
